/**
 * Pure helpers for sizing a Deposit Protection delegation against a
 * holder's current weekly tax burn.
 *
 * Why pure / no React: the same math powers two surfaces — the
 * `DepositProtectionInfoDialog` (which sets the cap when signing the
 * delegation) and the profile-side stale-warning ("your weekly tax
 * burn now exceeds your protection budget — refresh"). Keeping the
 * logic out of a hook lets the warning component diff "current burn"
 * vs. "signed cap" with the exact same formula, so users never see
 * the warning fire on a fresh delegation, or skip firing on a stale
 * one, due to two slightly different rounding rules.
 *
 * The 45% buffer is a deliberate trade-off, and its floor is set by
 * PERIOD PHASING, not price drift:
 *   - The grant's `ERC20PeriodTransferEnforcer` window (7 days) and the
 *     keeper's fill-to-target runway (7 days) are the same length and
 *     anchored at the same signing instant. A seat topped up to target
 *     at the start of a period re-crosses the 3-day gate mid-period,
 *     and that refill needs (target 7d + gate 3d) / 7d ≈ 1.43× the
 *     weekly burn — plus the 1% keeper fee ≈ 1.443×. The previous 20%
 *     buffer sat below this line, so a steady-state seat exhausted its
 *     allowance mid-period and the refill noop'd until the period
 *     rolled over, with the seat going dry seconds before the reset
 *     (grid-8 seat-108 incident).
 *   - Too high (≥ 50%): the wallet's permission UI shows numbers
 *     materially larger than what the user actually burns, defeating
 *     the "auto-sized so it isn't scary" goal.
 *   - 45% = the 1.443× phasing+fee floor rounded up, leaving a sliver
 *     for price drift. Larger price hikes / new tiles still surface
 *     the refresh nag — discovery's onchain-allowance clamp (see
 *     `protection-allowance.ts`) degrades those gracefully to partial
 *     top-ups instead of noops.
 */

import type { CoinSeatsData } from '@/components/seats/coin-seats-card';
import { getTaxTokenConfig } from '@/lib/drawer-utils';
import { formatUnits, type Address, getAddress, isAddress } from 'viem';

/** Buffer applied above raw weekly burn. Numerator/denominator so
 *  bigint arithmetic stays exact. ≥ (7d target + 3d gate)/7d × 1.01
 *  fee — see the phasing rationale in the module doc. */
export const PROTECTION_BUFFER_NUMERATOR = 29n;
export const PROTECTION_BUFFER_DENOMINATOR = 20n;
export const PROTECTION_BUFFER_PERCENT = 45;

export interface ProtectionBudget {
  /** Sum of `weeklyTax` (raw 6dp) across the holder's USDC-paid V2 seats. */
  weeklyUsdcRaw: bigint;
  /** Sum of `weeklyTax` (raw 18dp) across the holder's TAKEOVER-paid V2 seats. */
  weeklyTakeoverRaw: bigint;
  /** USDC cap to sign — `weeklyUsdcRaw * 1.45` rounded UP. */
  usdcCap: bigint;
  /** TAKEOVER cap to sign — `weeklyTakeoverRaw * 1.45` rounded UP. */
  takeoverCap: bigint;
  /** Convenience: total weekly burn in USD (TAKEOVER converted via
   *  `takeoverPriceUsd`; passes through 0 when no price is available
   *  so the dialog can fall back to a token-only label). */
  weeklyUsdEquivalent: number;
  /** Whether any V2 seat would be eligible for protection. Drives the
   *  "no tiles to protect" empty state in the dialog. V1 seats are
   *  excluded — the enforcer / redeemer only know about V2 grids. */
  hasProtectableSeat: boolean;
  /** Counts split by tax token. Useful for the dialog's per-token
   *  copy ("3 USDC tiles, 2 TAKEOVER tiles"). */
  usdcSeatCount: number;
  takeoverSeatCount: number;
}

/**
 * Apply the 45% buffer to a raw weekly burn.
 *
 * Ceiling rounding (`+ denom - 1`) — a buffer that floors loses cents
 * on small burns ($1.00 × 1.45 = $1.45 vs. floored $1) and the
 * resulting cap can drift below the actual top-up size for the
 * smallest seats.
 */
export function withBuffer(rawWeeklyBurn: bigint): bigint {
  if (rawWeeklyBurn <= 0n) return 0n;
  const num = rawWeeklyBurn * PROTECTION_BUFFER_NUMERATOR;
  return (num + PROTECTION_BUFFER_DENOMINATOR - 1n) / PROTECTION_BUFFER_DENOMINATOR;
}

/**
 * Sum weekly tax across the holder's V2 seats, splitting by tax token
 * so we can populate (or skip) the per-token caveats and permission
 * requests on the signing side.
 *
 * V1 grids are filtered out — they predate the enforcer / redeemer
 * deploy and have no protection path.
 *
 * Creator-on-unsold seats are filtered upstream by
 * `useLobbySeatHoldings`, so we don't need to repeat that here.
 */
export function computeProtectionBudget(
  groups: CoinSeatsData[],
  takeoverPriceUsd: number,
): ProtectionBudget {
  let weeklyUsdcRaw = 0n;
  let weeklyTakeoverRaw = 0n;
  let usdcSeatCount = 0;
  let takeoverSeatCount = 0;

  for (const g of groups) {
    if (!g.isV2Grid) continue;
    const ttc = getTaxTokenConfig(g.taxToken, undefined, g.taxTokenDecimals);
    for (const s of g.seats) {
      if (ttc.isTakeover) {
        weeklyTakeoverRaw += s.weeklyTax;
        takeoverSeatCount += 1;
      } else {
        weeklyUsdcRaw += s.weeklyTax;
        usdcSeatCount += 1;
      }
    }
  }

  const usdcUsd = Number(weeklyUsdcRaw) / 1e6;
  const takeoverUsd =
    takeoverPriceUsd > 0
      ? parseFloat(formatUnits(weeklyTakeoverRaw, 18)) * takeoverPriceUsd
      : 0;

  return {
    weeklyUsdcRaw,
    weeklyTakeoverRaw,
    usdcCap: withBuffer(weeklyUsdcRaw),
    takeoverCap: withBuffer(weeklyTakeoverRaw),
    weeklyUsdEquivalent: usdcUsd + takeoverUsd,
    hasProtectableSeat: usdcSeatCount + takeoverSeatCount > 0,
    usdcSeatCount,
    takeoverSeatCount,
  };
}

export interface ProtectionUgmRow {
  /** Checksummed UGM (manager) address. */
  ugm: Address;
  /** All boards on this UGM (one CoinSeatsData per grid id). */
  boards: CoinSeatsData[];
  /** Seat count summed across boards. */
  seatCount: number;
  /** Sum of `weeklyTax` (raw 6dp) across all USDC-paid seats. */
  weeklyUsdcRaw: bigint;
  /** Sum of `weeklyTax` (raw 18dp) across all TAKEOVER-paid seats. */
  weeklyTakeoverRaw: bigint;
}

/**
 * Aggregate the holder's V2 seats by `managerAddress` (UGM). Each
 * `CoinSeatsData` group already targets a single grid; the picker
 * surfaces UGMs (not grids) because `addDeposit` lives on the UGM
 * and the dialog's gating decisions (V2.1 vs V2.2+) are per-UGM,
 * not per-grid. V1 groups are filtered out — they predate the
 * enforcer and have no protection rail.
 *
 * Groups missing a `managerAddress` (shouldn't happen for V2 seats,
 * but the field is typed `string?` so static analysis can't prove it)
 * are skipped to avoid a phantom row keyed by `''`. A skipped group's
 * tax burn is therefore not eligible for protection — same outcome as
 * if the user toggled it off in the picker.
 */
export function groupSeatsByUgm(groups: CoinSeatsData[]): ProtectionUgmRow[] {
  const byUgm = new Map<string, ProtectionUgmRow>();
  for (const g of groups) {
    if (!g.isV2Grid) continue;
    if (!g.managerAddress || !isAddress(g.managerAddress)) continue;
    const key = g.managerAddress.toLowerCase();
    let row = byUgm.get(key);
    if (!row) {
      row = {
        ugm: getAddress(g.managerAddress),
        boards: [],
        seatCount: 0,
        weeklyUsdcRaw: 0n,
        weeklyTakeoverRaw: 0n,
      };
      byUgm.set(key, row);
    }
    row.boards.push(g);
    row.seatCount += g.seats.length;
    const ttc = getTaxTokenConfig(g.taxToken, undefined, g.taxTokenDecimals);
    for (const s of g.seats) {
      if (ttc.isTakeover) row.weeklyTakeoverRaw += s.weeklyTax;
      else row.weeklyUsdcRaw += s.weeklyTax;
    }
  }
  // Sort by descending seat count so the user's heaviest board lands
  // at the top of the picker — matches the ordering they're used to
  // from the lobby's "Your Seats" rail.
  return [...byUgm.values()].sort((a, b) => b.seatCount - a.seatCount);
}

/**
 * Re-derive a `ProtectionBudget` from the subset of `ProtectionUgmRow`
 * the user has toggled ON in the picker. The output is the EXACT same
 * shape the dialog already renders — the picker is purely additive
 * scaffolding around the existing copy. When `selectedUgms` is empty,
 * the returned budget has zeroed caps and `hasProtectableSeat: false`,
 * which the dialog uses to disable the Enable button.
 */
export function computeBudgetFromUgmSelection(
  rows: ProtectionUgmRow[],
  selectedUgms: ReadonlySet<string>,
  takeoverPriceUsd: number,
): ProtectionBudget {
  let weeklyUsdcRaw = 0n;
  let weeklyTakeoverRaw = 0n;
  let usdcSeatCount = 0;
  let takeoverSeatCount = 0;
  for (const row of rows) {
    if (!selectedUgms.has(row.ugm.toLowerCase())) continue;
    weeklyUsdcRaw += row.weeklyUsdcRaw;
    weeklyTakeoverRaw += row.weeklyTakeoverRaw;
    for (const g of row.boards) {
      const ttc = getTaxTokenConfig(g.taxToken, undefined, g.taxTokenDecimals);
      if (ttc.isTakeover) takeoverSeatCount += g.seats.length;
      else usdcSeatCount += g.seats.length;
    }
  }
  const usdcUsd = Number(weeklyUsdcRaw) / 1e6;
  const takeoverUsd =
    takeoverPriceUsd > 0
      ? parseFloat(formatUnits(weeklyTakeoverRaw, 18)) * takeoverPriceUsd
      : 0;
  return {
    weeklyUsdcRaw,
    weeklyTakeoverRaw,
    usdcCap: withBuffer(weeklyUsdcRaw),
    takeoverCap: withBuffer(weeklyTakeoverRaw),
    weeklyUsdEquivalent: usdcUsd + takeoverUsd,
    hasProtectableSeat: usdcSeatCount + takeoverSeatCount > 0,
    usdcSeatCount,
    takeoverSeatCount,
  };
}
